import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { addActiveChannel, listSavedKits, regenerateChannelGraphics } from "@/lib/api";
import { invalidateActiveChannelsNav } from "@/hooks/useActiveChannelsNav";
import { invalidateSavedKits, useSavedKitKeys } from "@/hooks/useSavedKits";
import RegenerateChannelKitDialog from "@/components/RegenerateChannelKitDialog";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.max(0, (Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString();
}

const SOURCE_BADGE = {
  auto_video: { label: "AUTO-VIDEO", classes: "text-yellow-300 border-yellow-400/40" },
  create_channel: { label: "CREATE CHANNEL", classes: "text-emerald-300 border-emerald-400/40" },
};

export default function SavedKits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenKit, setRegenKit] = useState(null);
  const { toggle } = useSavedKitKeys();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      invalidateSavedKits();
      setRows(await listSavedKits());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load saved kits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (row) => {
    try {
      await toggle({ kit_key: row.kit_key });
      toast.success(`Removed · ${row.name || "kit"}`);
      setRows((prev) => prev.filter((p) => p.kit_key !== row.kit_key));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };

  /** Prompt for the YouTube URL/@handle of the channel the user actually
   * launched from this kit, then register it for daily growth tracking.
   * The kit_id + niche label flow through so the Active Channels detail
   * page can show "Built from this kit" provenance. */
  const markLaunched = async (row) => {
    const handle = window.prompt(
      `Paste the YouTube URL or @handle for the channel you launched from "${row.name || "this kit"}":`,
      "",
    );
    if (!handle || !handle.trim()) return;
    try {
      const ch = await addActiveChannel({
        url_or_handle: handle.trim(),
        kit_id: row.kit_key,
        niche_label: row.niche || row.channel_name || null,
      });
      toast.success(`Tracking ${ch.name || "channel"} — first snapshot stored`);
      invalidateActiveChannelsNav();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't link this channel",
      );
    }
  };

  /** Re-roll banners + avatar for a saved create_channel-source kit
   * without leaving the Saved Kits page. Opens the same character +
   * style-preset picker the Create Channel gallery uses, then flips
   * the source row optimistically to a rendering state until the
   * next reload catches the new PNGs. auto_video-source kits use
   * their own regen flow on the auto-video detail page, so we hide
   * this action for them. */
  const runRegen = async (characterIds, stylePresetKey = null, providerKey = null) => {
    if (!regenKit) return;
    const c = regenKit;
    try {
      await regenerateChannelGraphics(c.kit_key, characterIds, stylePresetKey, providerKey);
      toast.success("Regenerating — this can take 30–60 seconds.");
      setRegenKit(null);
      // Reload after ~90s so the fresh banner shows without a manual refresh.
      setTimeout(() => { load(); }, 90_000);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't regenerate graphics",
      );
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto" data-testid="saved-kits-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 2 · SAVED KITS
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        Your channel kits.
      </h1>
      <p className="text-zinc-300 text-sm max-w-2xl mb-8 leading-relaxed">
        Every channel kit you've starred — banners, avatars, name suggestions,
        palettes, and about-page copy. Save anything you might launch later, or
        share with collaborators.
      </p>

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading<span className="blink">_</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-zinc-800 rounded-sm p-10 text-center" data-testid="kits-empty">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // NO SAVED KITS YET
          </div>
          <p className="text-zinc-300 text-sm max-w-md mx-auto mb-5">
            Run the auto-video pipeline from any Niche Playbook, or build a
            kit in <b>Create Channel</b>. Then hit{" "}
            <span className="font-mono text-yellow-400">☆ Save kit</span>.
          </p>
          <Link
            to="/auto-videos"
            data-testid="kits-empty-go"
            className="inline-block bg-yellow-400 text-black font-bold px-5 py-2.5 rounded-sm uppercase tracking-wide text-xs hover:bg-yellow-300 transition-colors"
          >
            ▸ Browse auto-videos
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kits-grid">
          {rows.map((r, i) => {
            const badge = SOURCE_BADGE[r.source_type] || SOURCE_BADGE.create_channel;
            const banner =
              r.images?.["banner_1.png"] ||
              r.data?.images?.["banner_1.png"];
            const avatar =
              r.images?.["avatar.png"] || r.data?.images?.["avatar.png"];
            const palette = r.data?.color_palette || {};
            const firstName = r.data?.channel_names?.[0]?.name || r.name;
            const firstHandle = r.data?.channel_names?.[0]?.handle;
            return (
              <div
                key={r.kit_key}
                data-testid={`kit-card-${r.kit_key}`}
                className="border border-zinc-800 hover:border-yellow-400/40 rounded-sm bg-black flex flex-col recon-row-in transition-colors overflow-hidden"
                style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
              >
                {/* Banner */}
                <div className="relative aspect-[2048/1152] bg-zinc-900 overflow-hidden">
                  {banner ? (
                    <img
                      src={`${API_BASE}${banner}`}
                      alt={firstName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-zinc-600 uppercase tracking-[0.2em]">
                      no banner
                    </div>
                  )}
                  <div
                    className={`absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.25em] bg-black/80 border px-2 py-0.5 rounded-sm ${badge.classes}`}
                  >
                    {badge.label}
                  </div>
                  <button
                    data-testid={`kit-remove-${r.kit_key}`}
                    onClick={() => remove(r)}
                    title="Remove from saved"
                    className="absolute top-2 right-2 font-mono text-xs text-zinc-300 bg-black/70 hover:text-red-400 hover:bg-black px-2 py-1 rounded-sm transition-colors"
                  >
                    ✕
                  </button>
                </div>

                {/* Body */}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    {avatar && (
                      <img
                        src={`${API_BASE}${avatar}`}
                        alt="avatar"
                        className="w-10 h-10 rounded-full border border-zinc-700 shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-display text-base font-bold truncate">
                        {firstName}
                      </div>
                      {firstHandle && (
                        <div className="font-mono text-[10px] text-zinc-400 truncate">
                          {firstHandle}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Palette */}
                  {Object.keys(palette).length > 0 && (
                    <div className="flex gap-1 mb-3">
                      {["primary", "secondary", "accent", "background"].map((k) =>
                        palette[k] ? (
                          <div
                            key={k}
                            className="w-6 h-6 rounded-sm border border-zinc-800"
                            style={{ background: palette[k] }}
                            title={`${k}: ${palette[k]}`}
                          />
                        ) : null
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t border-zinc-900">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 truncate min-w-0">
                      {r.niche || r.channel_name || "—"}
                      <br />
                      saved {timeAgo(r.saved_at)}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.source_type === "create_channel" && (
                        <button
                          data-testid={`kit-regen-${r.kit_key}`}
                          onClick={() => setRegenKit(r)}
                          title="Re-roll banners + avatar with a new style or characters"
                          className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-3 py-1.5 rounded-sm transition-colors"
                        >
                          ↻ Regenerate
                        </button>
                      )}
                      <button
                        data-testid={`kit-launch-${r.kit_key}`}
                        onClick={() => markLaunched(r)}
                        title="Link a real YouTube channel you launched from this kit"
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-zinc-800 hover:border-emerald-400 px-3 py-1.5 rounded-sm transition-colors"
                      >
                        ◉ Launched
                      </button>
                      {r.source_type === "auto_video" && r.source_id && (
                        <Link
                          to={`/auto-video/${r.source_id}`}
                          data-testid={`kit-open-${r.kit_key}`}
                          className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-3 py-1.5 rounded-sm transition-colors"
                        >
                          ▸ Open
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {regenKit && (
        <RegenerateChannelKitDialog
          kit={{
            job_id: regenKit.kit_key,
            name: regenKit.name || regenKit.channel_name || "kit",
            character_ids: regenKit.data?.character_ids || [],
            style_preset: regenKit.data?.style_preset || regenKit.data?.style_hint || null,
            banner_provider: regenKit.data?.banner_provider || null,
          }}
          onClose={() => setRegenKit(null)}
          onConfirm={runRegen}
        />
      )}
    </div>
  );
}
